import React, { useEffect, useRef, useState } from "react";
import { Search, Sparkles, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { searchProducts, slugify } from "../lib/api";

export const SearchBar = ({ size = "lg", autoFocus = false }) => {
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!q.trim()) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await searchProducts(q);
        setSuggestions(res);
      } catch (_) {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const go = (name) => {
    const slug = slugify(name);
    navigate(`/product/${slug}?q=${encodeURIComponent(name)}`);
    setOpen(false);
    setQ("");
  };

  const submit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    go(q.trim());
  };

  const isLg = size === "lg";

  return (
    <div ref={wrapRef} className="relative w-full">
      <form
        onSubmit={submit}
        className={`relative flex items-center gap-3 surface rounded-2xl transition-all duration-300 ${
          focused ? "border-[color:var(--lime)] shadow-[0_0_32px_rgba(168,255,62,0.25)]" : ""
        } ${isLg ? "px-5 py-4" : "px-4 py-2.5"}`}
        style={{ borderColor: focused ? "var(--lime)" : undefined }}
      >
        <Search className={`${isLg ? "h-6 w-6" : "h-4 w-4"} text-[color:var(--text-3)] flex-shrink-0`} strokeWidth={1.5} />
        <input
          data-testid="search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => { setFocused(true); setOpen(true); }}
          onBlur={() => setFocused(false)}
          placeholder={isLg ? "Search any food product — Nutella, Coca-Cola, Doritos..." : "Search products..."}
          autoFocus={autoFocus}
          className={`flex-1 bg-transparent outline-none text-[color:var(--text)] placeholder:text-[color:var(--text-3)] ${
            isLg ? "text-lg" : "text-sm"
          } font-body`}
        />
        {loading && <Loader2 className="h-4 w-4 animate-spin text-[color:var(--lime)]" />}
        <button
          data-testid="search-submit"
          type="submit"
          className={`lime-btn rounded-xl inline-flex items-center gap-2 ${isLg ? "px-5 py-2.5" : "px-3 py-1.5 text-sm"}`}
        >
          <Sparkles className={isLg ? "h-4 w-4" : "h-3.5 w-3.5"} />
          <span>Analyze</span>
        </button>
      </form>

      {open && q.trim() && (
        <div
          data-testid="search-dropdown"
          className="absolute left-0 right-0 top-full mt-2 surface rounded-xl overflow-hidden z-50 max-h-[320px] overflow-y-auto"
        >
          {suggestions.length === 0 && !loading && (
            <button
              type="button"
              onClick={() => go(q.trim())}
              className="w-full text-left px-4 py-3 hover:bg-white/[0.03] border-b border-white/5"
              data-testid="search-suggestion-analyze-new"
            >
              <div className="text-sm text-[color:var(--text)]">
                Analyze <span className="font-display font-semibold">"{q.trim()}"</span>
              </div>
              <div className="text-xs text-[color:var(--text-3)] mt-0.5">Ask the AI food scientist</div>
            </button>
          )}
          {suggestions.map((s, i) => (
            <button
              key={s.slug || i}
              type="button"
              data-testid={`search-suggestion-${i}`}
              onClick={() => go(s.name)}
              className="w-full text-left px-4 py-3 hover:bg-white/[0.03] border-b border-white/5 flex items-center justify-between gap-3"
            >
              <div>
                <div className="text-sm text-[color:var(--text)] font-medium">{s.name}</div>
                <div className="text-xs text-[color:var(--text-3)] mt-0.5">
                  {s.cached ? `Analyzed — score ${s.overallScore}` : s.preloaded ? "Popular product" : ""}
                </div>
              </div>
              {s.overallRating && (
                <span
                  className={`h-2.5 w-2.5 rounded-full ${
                    s.overallRating === "red" ? "dot-red" : s.overallRating === "yellow" ? "dot-yellow" : "dot-green"
                  }`}
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
